import { Link } from 'react-router-dom'
import { ChevronRight, ArrowRight } from 'lucide-react'
import { SECONDARY_CTA_TEXT } from '../../../config/cta'
import { FadeIn } from '../../../components/Motion'
import Seo from '../../../components/Seo'

const useCases = [
  { title: 'Customer Health Checks', description: 'Understand account sentiment beyond NPS scores' },
  { title: 'Onboarding Feedback', description: 'Learn where new customers struggle in their first 90 days' },
  { title: 'Churn Prevention', description: 'Surface at-risk accounts before renewal conversations' },
  { title: 'Feature Adoption', description: "Find out why users aren't adopting key features" },
  { title: 'Stakeholder Alignment', description: 'Capture feedback from multiple decision-makers per account' },
  { title: 'Competitive Intelligence', description: 'Understand why prospects choose alternatives' },
]

const exampleQuestions = [
  'Walk me through how your team uses our product day to day. What works well, and where do you hit friction?',
  'If you had to make the case to renew internally, what would you say—and what would give you pause?',
  'Tell me about a time you considered switching to a different solution. What triggered that?',
  'How has your experience with our support and customer success team been? Be honest.',
  'What is one thing we could fix that would make the biggest difference for your team?',
]

export default function B2BSaaS() {
  return (
    <div>
      <Seo
        title="Voice of Customer for B2B SaaS Products"
        description="Your customers have opinions your surveys never captured. PrismScope gives B2B SaaS teams the real story behind every account—before churn and before renewal goes sideways."
        path="/industries/technology/b2b-saas"
      />
      {/* Breadcrumbs */}
      <div className="bg-white border-b-2 border-intelligence-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/industries" className="hover:text-intelligence-900">Industries</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/industries/technology" className="hover:text-intelligence-900">Technology</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">VOC for B2B SaaS</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              Industry · Technology
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.98]">
              Voice of Customer for B2B SaaS
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-3xl mx-auto mb-10 leading-relaxed">
              Your customers have opinions about your product that no survey has ever captured.
              PrismScope gets you the real story behind every account.
            </p>
            <div className="relative inline-block group">
              <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-[#00008B] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" />
              <a href="https://rays.prismscope.ai/i/int__uznIuBr_RnnwTSyo1xp9Q?skip=1" className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-intelligence-900 border-2 border-intelligence-900 rounded-lg">
                Try a Live VOC Conversation <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* The Challenge */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">The challenge</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">The Challenge</h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="relative">
              <div aria-hidden="true" className="absolute inset-0 rounded-2xl bg-[#00008B] translate-x-2 translate-y-2" />
              <div className="relative bg-white border-2 border-intelligence-900 rounded-2xl p-8 md:p-10">
                <p className="text-lg text-intelligence-700 mb-6">
                  In B2B SaaS, the gap between what customers tell you and what they actually think is
                  where churn lives. Your NPS says 7. Your QBR says &ldquo;everything&apos;s fine.&rdquo;
                  And then the renewal conversation goes sideways.
                </p>
                <p className="text-lg text-intelligence-700 mb-6">
                  The problem is not that customers are hiding feedback. It is that your surveys never
                  ask the right follow-up:
                </p>
                <ul className="space-y-2 text-intelligence-600 mb-6">
                  <li>&bull; Why the admin stopped using the reporting module three months ago</li>
                  <li>&bull; What the champion tells their team about your product behind closed doors</li>
                  <li>&bull; Whether the &ldquo;fine&rdquo; in their CSAT response means satisfied or indifferent</li>
                  <li>&bull; What their procurement team will flag when renewal comes up</li>
                </ul>
                <p className="text-lg text-intelligence-900 font-medium text-center">
                  PrismScope asks the follow-ups your surveys never do.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">How it works</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">How It Works</h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="relative">
              <div aria-hidden="true" className="absolute inset-0 rounded-2xl bg-[#8b5cf6] translate-x-2 translate-y-2" />
              <div className="relative bg-white border-2 border-intelligence-900 rounded-2xl p-8 md:p-10">
                <p className="text-lg text-intelligence-700 mb-6">
                  Send your customers a PrismScope conversation instead of a static survey. The AI
                  adapts in real time&mdash;probing on frustrations, asking for specifics when answers
                  are vague, and connecting the dots across topics.
                </p>
                <p className="text-lg text-intelligence-700 mb-6">
                  Each conversation produces a full transcript, sentiment analysis, and theme
                  extraction. Across your full customer base, PrismScope synthesizes the patterns your
                  team needs to act on&mdash;before accounts go quiet.
                </p>
                <p className="text-lg text-intelligence-900 font-medium">
                  The result is voice-of-customer intelligence that is specific enough to act on and
                  broad enough to see trends.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">Use cases</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Use Cases</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-5">
            {useCases.map((uc, i) => {
              const colors = ['#3b82f6', '#8b5cf6', '#00008B']
              const color = colors[i % 3]
              return (
                <FadeIn key={uc.title} delay={i * 0.04}>
                  <div className="relative h-full">
                    <div aria-hidden="true" className="absolute inset-0 rounded-xl translate-x-1 translate-y-1" style={{ backgroundColor: color }} />
                    <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-5 h-full">
                      <h3 className="font-bold text-intelligence-900 mb-1">{uc.title}</h3>
                      <p className="text-intelligence-600 text-sm">{uc.description}</p>
                    </div>
                  </div>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* Example Questions */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">What we&apos;d ask</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Example Questions</h2>
            </div>
          </FadeIn>
          <div className="space-y-3">
            {exampleQuestions.map((q, i) => (
              <FadeIn key={i} delay={i * 0.04}>
                <div className="bg-white rounded-lg border-2 border-intelligence-900 p-5 shadow-[3px_3px_0_0_rgba(15,23,42,1)]">
                  <p className="text-intelligence-700 italic">&ldquo;{q}&rdquo;</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">Your move</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Hear what your customers actually think</h2>
            <p className="text-lg text-intelligence-400 mb-10">Try a live VOC conversation and see the difference adaptive follow-up makes.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="relative inline-block group">
                <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-[#3b82f6] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" />
                <a
                  href="https://rays.prismscope.ai/i/int__uznIuBr_RnnwTSyo1xp9Q?skip=1"
                  className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-intelligence-900 bg-white border-2 border-white rounded-lg"
                >
                  Try a Live VOC Conversation
                </a>
              </div>
              <div className="relative inline-block group">
                <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-[#8b5cf6] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" />
                <a
                  href="https://rays.prismscope.ai/i/int_ONF-JNX1Zq0CyFEt0jzyGA?skip=1"
                  className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-intelligence-900 border-2 border-intelligence-400 rounded-lg"
                >
                  {SECONDARY_CTA_TEXT}
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
